import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { EngagementGrid } from "@/components/calv/EngagementGrid";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/strategie-koordination")({
  head: () => {
    const h = pageHead("strategicCoordination", "de");
    return { meta: h.meta, links: h.links };
  },
  component: StrategiePage,
});

const topics = [
  {
    label: "01 · Marketing-Strategie & Channel-Mix",
    title: "Welche Kanäle tragen Ihr Geschäftsmodell wirtschaftlich?",
    body: "Methodische Budget-Allokation über SEA, SEO, Social, Affiliate, E-Mail. In-House-Aufbau vs. Agentur-Substanz. Channel-Mix für die Internationalisierung von DACH nach EU oder USA.",
  },
  {
    label: "02 · Tech-Stack & Daten-Architektur",
    title: "Shopify, WooCommerce oder Shopware — und welches CRM dahinter?",
    body: "Marketing-Automation, CDP, Server-Side-Tracking-Setups, die Plattform-Updates überleben. Custom vs. Standard. Wo KI sinnvoll integriert, wo Buzzword-Theater.",
  },
  {
    label: "03 · Agentur- & Dienstleister-Management",
    title: "RFP-Methodik, Performance-Reviews, Agentur-Wechsel.",
    body: "Substantielle Ausschreibungen, Bewertung laufender Partnerschaften, methodischer Wechsel ohne Werbe-Historie zu verlieren. Ownership von Konten und Daten.",
  },
  {
    label: "04 · Wachstum & Skalierung",
    title: "Wo liegt der eigentliche Skalierungs-Engpass?",
    body: "Traffic, Conversion-Rate, Margen, Operations. Zielkonflikt Wachstum vs. Effizienz. Plateau oder lokales Maximum. Investitionen über 12 vs. 36 Monate.",
  },
  {
    label: "05 · Krisensituationen & Plattform-Veränderungen",
    title: "Tracking-Verluste, Core-Updates, Penalty-Situationen.",
    body: "Datenschutz-Audits, Plattform-Banns. Schnelle, fundierte Einordnung statt Panik. Erfahrung aus vergleichbaren Situationen.",
  },
  {
    label: "06 · Organisation & KPI-Architektur",
    title: "Marketing-Team-Struktur, die in fünf Jahren trägt.",
    body: "KPIs für Geschäftsführer-Reporting vs. Operatives. Rhythmus zwischen Optimieren und Eskalieren. Verantwortlichkeiten, Überlappungen, Schnittstellen.",
  },
];

const engagements = [
  {
    label: "Strategy Review · einmalig",
    title: "3 bis 7 Tage über 2 bis 4 Wochen.",
    body: "Fundierte Bestandsaufnahme zu einer konkreten Fragestellung — Marketing-Setup, Tech-Stack, Agentur-Bewertung. Outcome: schriftliche Einordnung mit klaren Empfehlungen, optional Workshop-Präsentation.",
  },
  {
    label: "Quartals-Sparring · kontinuierlich",
    title: "1 bis 2 Tage pro Monat über 12 Monate.",
    body: "Jour-Fixe-Rhythmus, Begleitung laufender Entscheidungen, Präsenz bei strategischen Reviews, Sparring bei akuten Fragestellungen. Für eine unabhängige Stimme im Marketing-Lenkungskreis.",
  },
  {
    label: "RFP- & Auswahl-Begleitung · projektbezogen",
    title: "5 bis 15 Tage über 2 bis 4 Monate.",
    body: "Ausschreibung methodisch formulieren, Anbieter-Antworten bewerten, Vorstellungs-Gespräche begleiten. Für Agentur-Auswahl, Shop-Replatformings, CRM-Einführungen.",
  },
  {
    label: "Tech-Stack-Audit · einmalig",
    title: "5 bis 10 Tage über 3 bis 6 Wochen.",
    body: "Methodische Bewertung der bestehenden Marketing- und Daten-Infrastruktur — Tracking, Tools, Integrationen, Konfigurationen. Outcome: Befund-Bericht mit priorisiertem Maßnahmen-Plan.",
  },
  {
    label: "Krisen-Sparring · akut",
    title: "Tage bis Wochen, hochfrequent verfügbar.",
    body: "Bei Plattform-Updates, Tracking-Verlusten, Performance-Einbrüchen, Datenschutz-Konflikten. Schnelle Einordnung, methodische Diagnose, abgestimmte Sofort-Maßnahmen.",
  },
];

const steps = [
  {
    num: "01",
    title: "Einordnung der Lage",
    body: "Wir verstehen, wo Sie wirtschaftlich, technisch und organisatorisch stehen. Welche Ziele tragen, welche Annahmen halten der Überprüfung stand, welche Zielkonflikte sind real. Vorarbeit, nicht Formalismus — ohne diese Einordnung sind alle Empfehlungen Spekulation.",
  },
  {
    num: "02",
    title: "Priorisierung der Hebel",
    body: "In komplexen Setups ist nicht mangelnde Aktivität das Problem, sondern fehlende Priorisierung. Wir identifizieren die zwei bis vier Hebel mit dem höchsten wirtschaftlichen Effekt — und benennen explizit, was bewusst nicht verfolgt werden sollte.",
  },
  {
    num: "03",
    title: "Koordination der Akteure",
    body: "Wir übersetzen zwischen Geschäft und Technik, zwischen internen Teams und externen Dienstleistern, zwischen kurzfristigen und langfristigen Anforderungen. Wir machen Abhängigkeiten sichtbar, lösen Zuständigkeits-Lücken, richten Maßnahmen auf ein gemeinsames Ziel aus.",
  },
  {
    num: "04",
    title: "Kontinuierliche Einordnung",
    body: "Strategische Fragen entstehen nicht einmalig. Wir begleiten Entscheidungen über Wochen und Monate hinweg, in Jour-Fixe-Terminen, ad-hoc-Anfragen oder bei konkreten Eskalationen. Unser Anspruch ist Klarheit, nicht Komplexität.",
  },
];

const triggers = [
  {
    label: "Trigger 01",
    title: "Mehrere Dienstleister, keine übergreifende Steuerung.",
    body: "Performance-Agentur, SEO-Dienstleister, Web-Agentur, Shop-Anbieter und interne Teams arbeiten parallel — ohne dass jemand methodisch über die Schnittstellen schaut. Doppelarbeit, Lücken, Maßnahmen, die sich gegenseitig ausbremsen.",
  },
  {
    label: "Trigger 02",
    title: "Substanzielle Investitions-Entscheidung vor der Tür.",
    body: "Re-Platforming, CRM-Einführung, Agentur-Wechsel, Internationalisierung, Marketing-Team-Aufbau. Ohne externe Einordnung sind Sie auf Auskünfte derjenigen angewiesen, die ein Eigeninteresse am Ausgang haben.",
  },
  {
    label: "Trigger 03",
    title: "Wachstums-Plateau oder unklare Performance-Entwicklung.",
    body: "ROAS sinkt, Cost-per-Lead steigt, Conversion-Rate stagniert — ohne dass die Ursachen aus den verfügbaren Daten klar werden. Hier hilft eine methodische Außensicht, die nicht in operativen Routinen gefangen ist.",
  },
  {
    label: "Trigger 04",
    title: "Krisensituation mit Reaktionsbedarf.",
    body: "Plattform-Updates, Compliance-Konflikte, Daten-Migrations-Probleme, plötzliche Performance-Einbrüche. Methodische Einordnung ist wertvoller als Aktionismus — und schneller verfügbar als ein RFP-Prozess.",
  },
];

const exclusions = [
  {
    label: "Abgrenzung 01",
    title: "Keine Provisionen, keine Reseller-Modelle.",
    body: "Wir verkaufen keine Tools, keine Plattformen, keine Lizenzen. Unsere Empfehlung muss sich allein an Ihrem wirtschaftlichen Ergebnis bewähren — nicht an einer Margen-Vereinbarung mit einem Anbieter.",
  },
  {
    label: "Abgrenzung 02",
    title: "Keine Dauer-Beratung als Geschäftsmodell.",
    body: "Wir streben nicht an, dauerhaft anwesend zu bleiben. Wenn ein Projekt seinen Sparring-Bedarf erfüllt hat, lösen wir das Engagement methodisch sauber auf — auch wenn das die Auftragsgrundlage reduziert.",
  },
  {
    label: "Abgrenzung 03",
    title: "Keine Beratung ohne operative Substanz.",
    body: "Wir geben keine Empfehlungen zu Themen, in denen wir keine eigene Mandate-Erfahrung haben. Wo unsere Substanz endet, sagen wir das offen — und verweisen auf substantielle Externe, ohne Provisionen für die Vermittlung.",
  },
];

function StrategiePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Strategie & Koordination · seit 2013"
          headline={<>Strategisches Sparring, <em>operativ verankert.</em></>}
          lead="Wir begleiten Geschäftsführung und Marketing-Leitung in komplexen Digital-Setups — bei Channel-Mix-Entscheidungen, Tech-Stack-Auswahl, Agentur-Koordination und in Krisensituationen. Unabhängig von Provisionen, Reseller-Verträgen oder Vertriebs-Interessen. Operative Tiefe aus eigener Mandate-Arbeit."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Strategie & Koordination" }]}
        />

        <Section number="02" kicker="Wer wir sind" headline={<>Beratung aus <em>operativer Substanz</em>, nicht aus Foliensätzen.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Klassische Strategie-Beratung trennt Denker von Machern — Folien werden präsentiert, Umsetzung liegt anderswo. Operative Agenturen wiederum haben Eigeninteressen: jede Empfehlung läuft Gefahr, die eigene Auftragsgrundlage zu vergrößern.</p>
              <p className="mb-5">Wir arbeiten anders. Calvarius betreibt seit 2013 die operative Substanz selbst — Performance Marketing, SEO, Tracking, Web-Entwicklung. Diese Erfahrung bringen wir ins Sparring ein. Wir wissen, wie ein Server-Side-Tracking-Setup wirklich aufgesetzt wird, was eine Performance-Max-Kampagne tatsächlich kostet, wann eine Shop-Migration sechs Monate länger dauert als geplant.</p>
              <p>Das macht uns nicht zur klassischen Beratung. Wir liefern keine Strategie-Decks, sondern Einordnungen, die in Ihrer Wirklichkeit standhalten. Und wir nehmen keine Provisionen, keine Reseller-Margen, keine Vertriebs-Boni — unsere Empfehlung muss sich allein an Ihrem wirtschaftlichen Ergebnis bewähren.</p>
            </div>
          </RevealOnScroll>
          <Callout>Strategie ohne operative Tiefe ist Wunschdenken. Operative Arbeit ohne strategische Einordnung ist Aktionismus. Beides zusammen ist Substanz.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Themen, bei denen wir typischerweise sparren" headline={<>Konkrete Fragestellungen, <em>nicht generische Beratung.</em></>} caption="Sechs Cluster, in denen Calvarius regelmäßig als Sparringspartner gerufen wird. Die Liste ist nicht abschließend, aber sie zeigt, wo unsere operative Erfahrung am direktesten trägt.">
          <EngagementGrid items={topics} />
        </Section>

        <Section number="04" kicker="Engagement-Modelle" headline={<>Konkrete <em>Formate</em> für konkrete Situationen.</>} caption="Strategisches Sparring ist kein festes Produkt — Engagement-Tiefe und -Dauer richten sich nach Ihrer Situation. Welches Format passt, klären wir im Erstgespräch.">
          <EngagementGrid items={engagements} />
        </Section>

        <Section variant="tinted" number="05" kicker="Vorgehen" headline={<>Vier Phasen, <em>belastbar in der Umsetzung.</em></>}>
          <ProcessFlow steps={steps} />
          <Callout>Gute Entscheidungen entstehen nicht durch mehr Meinungen — sondern durch bessere Einordnung.</Callout>
        </Section>

        <Section number="06" kicker="Wann diese Rolle besonders trägt" headline={<>Vier Trigger-Situationen, <em>in denen Sparring wirklich Hebel hat.</em></>}>
          <EngagementGrid items={triggers} />
        </Section>

        <Section variant="tinted" number="07" kicker="Was wir bewusst nicht machen" headline={<>Methodische <em>Ehrlichkeit</em> statt Generalisten-Versprechen.</>}>
          <EngagementGrid items={exclusions} />
        </Section>

        <LogoReferences locale="de" number="08" />

        <RelatedServices
          locale="de"
          fromId="strategie-koordination"
          number="09"
          items={relatedBySource["strategie-koordination"]}
        />

        <Section number="10" kicker="Kostenfrei · circa 30 Minuten">
          <CTABlock
            headline={<>Erstgespräch zu Ihrer <em>konkreten Situation.</em></>}
            body="Im Erstgespräch klären wir methodisch, ob ein Sparring-Engagement zu Ihrer Lage passt — und in welchem Format. Sie skizzieren Ihre Situation, wir geben eine erste Einordnung. Falls beidseitig sinnvoll: Vorschlag eines passenden Engagement-Modells. Falls wir nicht die richtigen sind, sagen wir das offen."
            primary={{ label: "Strategiegespräch anfragen", to: "/kontakt" }}
            secondary={{ label: "Werkzeuge testen", to: "/tools" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
